import React, { useState, useRef, useEffect } from 'react';
import { Send, Check, CheckCheck, XCircle, Paperclip, File, Download, Eye, X, Image as ImageIcon, Loader2, MessageCircle, Mic, Volume2 } from 'lucide-react';
import ModalShell, { MODAL_SUBMIT_CLASS } from '../common/ModalShell';
import VoiceRecorder from './VoiceRecorder';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
// Used in the voice-note upload's .catch, but never imported — so a FAILED
// upload threw "toast is not defined" from inside its own error handler and the
// user was told nothing at all.
import { toast } from 'react-toastify';
import baseURL from '../../utils/baseURL';
import { formatTime } from '../../utils/clock';
import { userSocketEvents } from '../../services/supportSocketService';

import getFileURL from '../../utils/fileURL';


// Which side of the chat a message sits on. The server stamps every row with
// senderSide ('raiser' | 'support' | 'system'); rows written before that column
// existed fall back to a role comparison. Module-level on purpose so the effect
// that formats history does not gain a new dependency.
const isRaiserMessage = (msg, userRole) => (msg?.senderSide ? msg.senderSide === 'raiser' : msg?.senderRole === userRole);

const SupportChatInterface = ({
    initialQuestion,
    category,
    subCategory,
    description,
    categoryId,
    subCategoryId,
    requestId,
    ticketId: initialTicketId
}) => {
    const { t } = useTranslation(['support', 'chat']);
    const userId = localStorage.getItem('ID');
    const userRole = localStorage.getItem('Role');

    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [inputError, setInputError] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [ticketId, setTicketId] = useState(initialTicketId || null);
    const [isTicketClosed, setIsTicketClosed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Initialize socket and fetch history
    useEffect(() => {
        if (!ticketId || !userId || !userRole) {
            setIsLoading(false);
            return;
        }

        // Fetch history
        userSocketEvents.fetchHistory(ticketId, 1);

        // Listen for reconnect to re-fetch history
        userSocketEvents.onReconnect(() => {
            console.log('🔄 Socket reconnected, re-fetching chat history for ticket:', ticketId);
            userSocketEvents.fetchHistory(ticketId, 1);
        });

        // Listen for history
        userSocketEvents.onHistory((data) => {
            if (data.ticketId === ticketId) {
                const formattedMessages = (data.messages || []).map(msg => ({
                    ...msg,
                    id: msg.messageId || msg._id,
                    type: isRaiserMessage(msg, userRole) ? 'user' : 'support',
                    content: msg.message,
                    timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
                    attachments: msg.attachments || []
                }));
                setMessages(prev => {
                    const merged = [...formattedMessages];
                    prev.forEach(m => {
                        const id = m.id || m._id;
                        if (!formattedMessages.find(fm => (fm.id || fm._id) === id)) {
                            merged.push(m);
                        }
                    });
                    return merged.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                });
                setIsLoading(false);
            }
        });

        // Listen for new messages
        userSocketEvents.onReceiveMessage((data) => {
            if (data.ticketId === ticketId) {
                const msgId = data.messageId || data._id;
                const newMessage = {
                    ...data,
                    id: msgId,
                    type: isRaiserMessage(data, userRole) ? 'user' : 'support',
                    content: data.message,
                    timestamp: data.createdAt ? new Date(data.createdAt) : new Date(),
                    attachments: data.attachments || []
                };
                setMessages(prev => {
                    // Skip if this message already exists (optimistic add or duplicate)
                    const isDuplicate = prev.some(m =>
                        (m.id && msgId && String(m.id) === String(msgId)) ||
                        (m.senderId === data.senderId && m.content === data.message && m.type === 'user' && Math.abs(new Date(m.timestamp) - new Date(newMessage.timestamp)) < 5000)
                    );
                    if (isDuplicate) return prev;
                    return [...prev, newMessage];
                });
            }
        });

        // Listen for ticket closed
        userSocketEvents.onTicketClosed((data) => {
            if (data.ticketId === ticketId) {
                setIsTicketClosed(true);
            }
        });

        // Set interval to check for ticket status regularly as fallback
        const interval = setInterval(() => {
            if (!isTicketClosed) {
                // You could add a check status socket event here if needed
            }
        }, 5000);

        return () => {
            clearInterval(interval);
            userSocketEvents.removeListener('supportHistory');
            userSocketEvents.removeListener('receiveSupportMessage');
            userSocketEvents.removeListener('supportTicketClosed');
            userSocketEvents.removeListener('reconnect');
        };
    }, [ticketId, userId, userRole]);


    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + selectedFiles.length > 5) {
            setInputError(t('chat.max_files_limit'));
            return;
        }

        const validFiles = files.filter(file => {
            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
                setInputError(t('chat.file_too_large'));
            }
            return isLt10M;
        });

        setSelectedFiles(prev => [...prev, ...validFiles]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Authenticated download. The endpoint requires a token, so this cannot be a
    // window.open(): a plain browser navigation carries no Authorization header.
    // Fetch it, then hand the blob to a temporary link — same pattern as the
    // doctor/patient chat download.
    const handleDownload = async (fileUrl) => {
        if (!fileUrl) return;
        const fileName = fileUrl.split('/').pop();
        try {
            const response = await fetch(`${baseURL}/support/message/download/${fileName}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
            });
            if (!response.ok) throw new Error('Download failed');
            const url = window.URL.createObjectURL(await response.blob());
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Support attachment download failed:', err);
        }
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if ((!input.trim() && selectedFiles.length === 0) || isTicketClosed || isLoading || isUploading) return;

        const messageText = input.trim();
        const currentFiles = [...selectedFiles];

        if (currentFiles.length > 0) {
            setIsUploading(true);
            try {
                const formData = new FormData();
                formData.append('ticketId', ticketId);
                formData.append('message', messageText);

                currentFiles.forEach(file => {
                    formData.append('files', file); // Backend expects 'images' for support chat
                });

                const response = await axios.post(`${baseURL}/support/message/send`, formData, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                        'Content-Type': 'multipart/form-data',
                    },
                });
                console.log("Support message sent with files:", response.data);

                setInput('');
                setSelectedFiles([]);
                setInputError('');
            } catch (error) {
                console.error("Error sending support message with files:", error.response?.data || error.message);
                setInputError(t('support.send_failed'));
            } finally {
                setIsUploading(false);
            }
        } else {
            setInput('');
            setSelectedFiles([]);
            setInputError('');

            const newMessage = {
                id: Date.now(),
                type: 'user',
                content: messageText,
                timestamp: new Date(),
                status: 'sent',
                senderId: userId,
                senderRole: userRole,
                senderSide: 'raiser',
                attachments: []
            };

            setMessages(prev => [...prev, newMessage]);

            // The sender is the socket's verified identity; only ticket + text go over.
            userSocketEvents.sendMessage({
                ticketId,
                message: messageText
            });
        }
    };

    return (
        <div data-testid="support-chat-container" className="flex flex-col h-full bg-gradient-to-b from-gray-50 to-white relative">

            {/* Image Preview Modal */}
            {previewImage && (
                // Click on the dimmed page closes, as before the shared frame.
                <div onClick={(e) => { if (e.target === e.currentTarget.firstElementChild) setPreviewImage(null); }}>
                    <ModalShell
                        title={t('supportChat.preview')}
                        onClose={() => setPreviewImage(null)}
                        size="5xl"
                        zClass="z-[9999]"
                        testId="support-chat-image-preview-modal"
                        closeTestId="support-chat-image-preview-close-button"
                        footer={(
                            <button
                                type="button"
                                onClick={() => handleDownload(previewImage)}
                                className={MODAL_SUBMIT_CLASS}
                                data-testid="support-chat-image-download-button"
                            >
                                <Download size={18} data-testid="support-chat-image-download-icon" />
                                {t('chat.download', 'Download')}
                            </button>
                        )}
                    >
                        <div className="flex items-center justify-center">
                            <img
                                src={previewImage}
                                alt={t('supportChat.preview')}
                                className="max-w-full max-h-[65vh] object-contain rounded-lg"
                                data-testid="support-chat-image-preview"
                            />
                        </div>
                    </ModalShell>
                </div>
            )}

            {/* Header / Ticket Info */}
            {ticketId && (
                <div className="bg-white border-b border-gray-100 px-4 py-2 flex items-center justify-between shadow-sm z-10" data-testid="support-chat-header">
                    <div className="flex items-center gap-2" data-testid="support-chat-ticket-info">
                        <span className="text-xs font-semibold text-gray-400 uppercase" data-testid="support-chat-ticket-label">{t('supportChat.ticketId')}:</span>
                        <span className="text-xs font-bold text-[#4ABDBD] bg-[#4ABDBD]/10 px-2 py-0.5 rounded" data-testid="support-chat-ticket-id-text">{ticketId}</span>
                    </div>
                    {isTicketClosed ? (
                        <div className="flex items-center gap-1.5 text-xs font-medium text-red-500 bg-red-50 px-2.5 py-1 rounded-full border border-red-100" data-testid="support-chat-ticket-closed-badge">
                            <XCircle size={14} data-testid="support-chat-ticket-closed-icon" />
                            <span data-testid="support-chat-ticket-closed-text">{t('support.ticket_closed')}</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-1.5 text-xs font-medium text-green-500 bg-green-50 px-2.5 py-1 rounded-full border border-green-100" data-testid="support-chat-ticket-active-badge">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" data-testid="support-chat-ticket-active-indicator"></span>
                            <span data-testid="support-chat-ticket-active-text">{t('support.active')}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6 custom-scrollbar bg-[rgba(255,255,255,0.4)]" data-testid="support-chat-messages-area">
                {isLoading ? (
                    <div className="flex items-center justify-center h-full" data-testid="support-chat-loading-container">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="w-8 h-8 text-[#4ABDBD] animate-spin" data-testid="support-chat-loading-icon" />
                            <p className="text-sm text-gray-500 animate-pulse" data-testid="support-chat-loading-text">{t('support.loading_messages')}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {messages.length === 0 && (
                            <div className="flex flex-col items-center justify-center h-full opacity-30 select-none" data-testid="support-chat-empty-state">
                                <MessageCircle size={64} className="text-gray-300 mb-4" data-testid="support-chat-empty-icon" />
                                <p className="text-sm text-gray-500 font-medium" data-testid="support-chat-empty-text">{t('support.no_messages_yet')}</p>
                            </div>
                        )}
                        {messages.map((msg, index) => (
                            <div
                                key={msg.id || index}
                                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
                                data-testid={`support-chat-message-${msg.id || index}`}
                            >
                                <div className={`max-w-[80%] ${msg.type === 'user' ? 'flex flex-col items-end' : 'flex flex-col items-start'}`}>
                                    {/* Sender Name (only for received messages) */}
                                    {msg.type !== 'user' && (
                                        <span className="text-[10px] font-bold text-[#4ABDBD] mb-1 px-1" data-testid={`support-chat-sender-name-${msg.id || index}`}>
                                            {msg.senderName || (msg.senderRole === 'bot' ? t('supportChat.supportBot') : (msg.senderRole === 'admin' ? t('supportChat.admin') : (msg.senderRole || t('supportChat.support'))))}
                                        </span>
                                    )}
                                    <div
                                        className={`rounded-2xl px-4 py-3 shadow-sm relative group ${msg.type === 'user'
                                            ? 'bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] text-white rounded-tr-none'
                                            : 'bg-white text-gray-800 border border-gray-100 rounded-tl-none shadow-md'
                                            }`}
                                        data-testid={`support-chat-message-bubble-${msg.id || index}`}
                                    >
                                        <p className="text-[15px] whitespace-pre-wrap break-words leading-relaxed font-normal" data-testid={`support-chat-message-content-${msg.id || index}`}>{msg.content}</p>

                                        {/* Attachments */}
                                        {msg.attachments && msg.attachments.length > 0 && (
                                            <div className="mt-3 grid gap-2 min-w-[200px]" data-testid={`support-chat-attachments-${msg.id || index}`}>
                                                {msg.attachments.map((file, i) => (
                                                    <div key={i} className={`rounded-xl overflow-hidden border ${msg.type === 'user' ? 'border-white/20 bg-white/10' : 'border-gray-100 bg-gray-50'}`} data-testid={`support-chat-attachment-${msg.id || index}-${i}`}>
                                                        {file.fileType === 'image' || (file.mimeType && file.mimeType.startsWith('image/')) ? (
                                                            <div className="relative group cursor-pointer" onClick={() => setPreviewImage(getFileURL(file.url))} data-testid={`support-chat-image-attachment-${msg.id || index}-${i}`}>
                                                                <img
                                                                    src={getFileURL(file.url)}
                                                                    alt={file.fileName || t('chat.image_alt', 'Image')}
                                                                    className="max-w-full h-auto max-h-52 object-contain mx-auto"
                                                                    data-testid={`support-chat-image-${msg.id || index}-${i}`}
                                                                />
                                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                                                    <button className="p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 shadow-sm transition-all scale-90 group-hover:scale-100" data-testid={`support-chat-image-view-button-${msg.id || index}-${i}`}>
                                                                        <Eye size={20} data-testid={`support-chat-image-view-icon-${msg.id || index}-${i}`} />
                                                                    </button>
                                                                    <button
                                                                        className="p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 shadow-sm transition-all scale-90 group-hover:scale-100"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDownload(file.url);
                                                                        }}
                                                                        data-testid={`support-chat-image-download-button-${msg.id || index}-${i}`}
                                                                    >
                                                                        <Download size={20} data-testid={`support-chat-image-download-icon-${msg.id || index}-${i}`} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : file.fileType === 'voice' || (file.mimeType && file.mimeType.startsWith('audio/')) ? (
                                                            <div className={`p-3 min-w-[240px] ${msg.type === 'user' ? 'bg-white/10' : 'bg-[#4ABDBD]/5'} rounded-xl`} data-testid={`support-chat-voice-attachment-${msg.id || index}-${i}`}>
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <Volume2 size={16} className={msg.type === 'user' ? 'text-white' : 'text-[#4ABDBD]'} data-testid={`support-chat-voice-icon-${msg.id || index}-${i}`} />
                                                                    <span className={`text-xs font-bold ${msg.type === 'user' ? 'text-white' : 'text-gray-700'}`} data-testid={`support-chat-voice-label-${msg.id || index}-${i}`}>
                                                                        {t('chat.voice_message')}
                                                                    </span>
                                                                </div>
                                                                <audio
                                                                    src={getFileURL(file.url)}
                                                                    controls
                                                                    className={`w-[255px] h-12 custom-audio-player ${msg.type === 'user' ? 'invert brightness-200' : ''}`}
                                                                    data-testid={`support-chat-voice-player-${msg.id || index}-${i}`}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className="p-3 flex items-center gap-3" data-testid={`support-chat-file-attachment-${msg.id || index}-${i}`}>
                                                                <div className={`p-2 rounded-lg ${msg.type === 'user' ? 'bg-white/20' : 'bg-white shadow-sm'}`}>
                                                                    <File size={20} data-testid={`support-chat-file-icon-${msg.id || index}-${i}`} />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-xs font-bold truncate" data-testid={`support-chat-file-name-${msg.id || index}-${i}`}>{file.fileName}</p>
                                                                    <p className="text-[10px] opacity-60 uppercase" data-testid={`support-chat-file-type-${msg.id || index}-${i}`}>{file.mimeType ? file.mimeType.split('/')[1] : t('supportChat.file')}</p>
                                                                </div>
                                                                <button
                                                                    className={`p-2 rounded-lg transition-colors ${msg.type === 'user' ? 'hover:bg-white/20' : 'hover:bg-gray-200'}`}
                                                                    onClick={() => handleDownload(file.url)}
                                                                    data-testid={`support-chat-file-download-button-${msg.id || index}-${i}`}
                                                                >
                                                                    <Download size={18} data-testid={`support-chat-file-download-icon-${msg.id || index}-${i}`} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className={`flex items-center gap-1.5 mt-1.5 px-1 ${msg.type === 'user' ? 'flex-row-reverse' : ''}`} data-testid={`support-chat-message-meta-${msg.id || index}`}>
                                        <span className={`text-[10px] font-medium text-gray-400`} data-testid={`support-chat-message-timestamp-${msg.id || index}`}>
                                            {formatTime(msg.timestamp)}
                                        </span>
                                        {msg.type === 'user' && (
                                            <span className="text-[#4ABDBD]" data-testid={`support-chat-message-status-${msg.id || index}`}>
                                                {msg.status === 'read' ? <CheckCheck size={14} data-testid={`support-chat-message-status-read-icon-${msg.id || index}`} /> : <Check size={14} className="opacity-60" data-testid={`support-chat-message-status-sent-icon-${msg.id || index}`} />}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className={`bg-white border-t border-gray-100 shadow-[0_-4px_20px_rgba(0,0,0,0.03)] z-10 transition-all ${isTicketClosed ? 'opacity-50 grayscale' : ''}`} data-testid="support-chat-input-area">

                {/* File Previews */}
                {selectedFiles.length > 0 && (
                    <div className="px-4 py-3 bg-gray-50 flex flex-wrap gap-2 border-b border-gray-100" data-testid="support-chat-file-previews-container">
                        {selectedFiles.map((file, index) => (
                            <div key={index} className="relative group bg-white border border-gray-200 rounded-xl p-2 pr-9 flex items-center gap-2 shadow-sm animate-in zoom-in-95 duration-200" data-testid={`support-chat-file-preview-${index}`}>
                                {file.type.startsWith('image/') ? (
                                    <div className="w-9 h-9 rounded-lg bg-[#4ABDBD]/5 flex items-center justify-center overflow-hidden border border-[#4ABDBD]/10">
                                        <ImageIcon size={18} className="text-[#4ABDBD]" data-testid={`support-chat-file-preview-image-icon-${index}`} />
                                    </div>
                                ) : (
                                    <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center border border-gray-100">
                                        <File size={18} className="text-gray-400" data-testid={`support-chat-file-preview-file-icon-${index}`} />
                                    </div>
                                )}
                                <div className="flex flex-col min-w-0">
                                    <span className="text-[11px] font-bold text-gray-700 truncate max-w-[120px]" data-testid={`support-chat-file-preview-name-${index}`}>{file.name}</span>
                                    <span className="text-[9px] text-gray-400 font-medium uppercase" data-testid={`support-chat-file-preview-size-${index}`}>{(file.size / 1024).toFixed(0)} KB</span>
                                </div>
                                <button
                                    onClick={() => removeFile(index)}
                                    className="absolute right-1 text-gray-300 hover:text-red-500 p-1.5 transition-colors"
                                    data-testid={`support-chat-file-preview-remove-button-${index}`}
                                >
                                    <X size={16} data-testid={`support-chat-file-preview-remove-icon-${index}`} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <form onSubmit={handleSend} className="p-4 flex items-end gap-3 max-w-7xl mx-auto w-full" data-testid="support-chat-form">
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple
                        className="hidden"
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                        data-testid="support-chat-file-input"
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isTicketClosed}
                        className="p-3.5 text-gray-400 hover:text-[#4ABDBD] hover:bg-[#4ABDBD]/5 rounded-2xl transition-all border border-gray-100 disabled:opacity-50 disabled:cursor-not-allowed group"
                        title={t('chat.attach_files')}
                        data-testid="support-chat-attach-button"
                    >
                        <Paperclip size={22} className="group-hover:rotate-12 transition-transform" data-testid="support-chat-attach-icon" />
                    </button>

                    <div className="flex-1 relative flex flex-col">
                        <textarea
                            ref={inputRef}
                            rows="1"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend(e);
                                }
                            }}
                            placeholder={isTicketClosed ? t('support.ticket_is_closed') : t('support.type_a_message')}
                            disabled={isTicketClosed || !ticketId || isUploading}
                            className={`w-full max-h-32 min-h-[52px] px-4 py-3.5 bg-gray-50/50 rounded-2xl border-2 focus:outline-none focus:ring-4 focus:ring-[#4ABDBD]/5 focus:border-[#4ABDBD] text-[15px] transition-all resize-none custom-scrollbar disabled:opacity-50 disabled:cursor-not-allowed ${inputError
                                ? 'border-red-200 bg-red-50/50'
                                : 'border-transparent'
                                }`}
                            data-testid="support-chat-message-input"
                        />
                        {inputError && (
                            <p className="absolute -top-6 left-2 text-[10px] text-red-500 font-bold bg-white px-2 py-0.5 rounded shadow-sm border border-red-50" data-testid="support-chat-input-error">{inputError}</p>
                        )}
                    </div>

                    {input.trim() || selectedFiles.length > 0 ? (
                        <button
                            type="submit"
                            disabled={(!input.trim() && selectedFiles.length === 0) || isTicketClosed || !ticketId || isUploading}
                            className={`h-[52px] w-[52px] flex items-center justify-center rounded-2xl transition-all shadow-lg hover:shadow-[#4ABDBD]/20 ${(!input.trim() && selectedFiles.length === 0) || isTicketClosed || !ticketId || isUploading
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] text-white hover:scale-105 active:scale-95'
                                }`}
                            data-testid="support-chat-send-button"
                        >
                            {isUploading ? (
                                <Loader2 size={22} className="animate-spin" data-testid="support-chat-send-loading-icon" />
                            ) : (
                                <Send size={22} className={input.trim() ? "translate-x-0.5 -translate-y-0.5" : ""} data-testid="support-chat-send-icon" />
                            )}
                        </button>
                    ) : (
                        <VoiceRecorder
                            onRecordingComplete={(file) => {
                                // Handle voice recording completion by sending it immediately
                                const formData = new FormData();
                                formData.append('ticketId', ticketId);
                                formData.append('files', file);

                                setIsUploading(true);
                                axios.post(`${baseURL}/support/message/send`, formData, {
                                    headers: {
                                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                                        'Content-Type': 'multipart/form-data',
                                    },
                                }).then(() => {
                                    // Refresh or handle success
                                }).catch(err => {
                                    console.error("Error sending voice note:", err);
                                    toast.warning(t('support.send_failed'));
                                }).finally(() => {
                                    setIsUploading(false);
                                });
                            }}
                            isUploading={isUploading}
                            disabled={isTicketClosed || !ticketId}
                        />
                    )}
                </form>
            </div>
        </div>
    );
};

export default SupportChatInterface;
